import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { groupsAPI, subscriptionAPI, facilitatorAPI, groupsExtendedAPI, reportsAPI } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Plus, Lock, UserPlus, LogOut, ArrowRight, ClipboardList, CheckCircle, XCircle, Clock, Shield, Rocket, Pencil, PartyPopper, ChevronDown, ChevronUp, UserX, UserCheck, Eye, PauseCircle, PlayCircle, Calendar, Flag, AlertTriangle, FileText, User, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const GROUP_THEMES = [
  "Morning Routines",
  "Managing Overwhelm",
  "Career Transitions",
  "Relationship Growth",
  "Creative Blocks",
  "Anxiety & Stress",
  "Building Confidence",
  "Work-Life Balance",
  "Personal Development",
  "Other"
];

export default function GroupsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [facilitatorStatus, setFacilitatorStatus] = useState(null);
  const [applications, setApplications] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("my-groups");
  const [reviewingApp, setReviewingApp] = useState(null);
  const [adminMessage, setAdminMessage] = useState('');
  const [expandedApp, setExpandedApp] = useState(null);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokingFacilitator, setRevokingFacilitator] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendingFacilitator, setSuspendingFacilitator] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendReturnDate, setSuspendReturnDate] = useState('');
  const [reports, setReports] = useState([]);
  const [expandedReport, setExpandedReport] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  
  const [newGroupData, setNewGroupData] = useState({
    name: '',
    description: '',
    theme: '',
    max_members: 10
  });
  
  const [applicationData, setApplicationData] = useState({
    reason: '',
    experience: '',
    proposed_group_name: '',
    proposed_group_theme: '',
    proposed_group_description: ''
  });

  // Define loadGroups and loadFacilitatorStatus BEFORE checkAccessAndLoad to avoid temporal dead zone
  const loadGroups = useCallback(async () => {
    try {
      const [allRes, myRes] = await Promise.all([
        groupsAPI.getAll(),
        groupsAPI.getMyGroups()
      ]);
      setGroups(allRes.data);
      setMyGroups(myRes.data);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  }, []);

  const loadFacilitatorStatus = useCallback(async () => {
    try {
      const res = await facilitatorAPI.getStatus();
      setFacilitatorStatus(res.data);
      
      // If admin, load applications, facilitators, and reports
      if (res.data.is_admin) {
        const [appsRes, facilitatorsRes, reportsRes] = await Promise.all([
          facilitatorAPI.getApplications(),
          facilitatorAPI.getFacilitators(),
          reportsAPI.getAll()
        ]);
        setApplications(appsRes.data);
        setFacilitators(facilitatorsRes.data);
        setReports(reportsRes.data);
      }
      
      // If has approved application awaiting acceptance, show dialog
      if (res.data.has_approved_application && !res.data.approved_application?.facilitator_accepted) {
        setApprovalDialogOpen(true);
      }
    } catch (error) {
      console.error('Failed to load facilitator status:', error);
    }
  }, []);

  const checkAccessAndLoad = useCallback(async () => {
    try {
      const usageRes = await subscriptionAPI.getUsage();
      const access = usageRes.data.has_small_groups;
      setHasAccess(access);
      
      if (access) {
        await loadGroups();
        await loadFacilitatorStatus();
      }
    } catch (error) {
      console.error('Failed to check access:', error);
    } finally {
      setLoading(false);
    }
  }, [loadGroups, loadFacilitatorStatus]);

  useEffect(() => {
    checkAccessAndLoad();
  }, [checkAccessAndLoad]);

  const handleResolveReport = async (reportId, status) => {
    try {
      await reportsAPI.resolve(reportId, status, resolveNotes);
      toast.success(`Report ${status}`);
      setExpandedReport(null);
      setResolveNotes('');
      // Reload reports
      const reportsRes = await reportsAPI.getAll();
      setReports(reportsRes.data);
    } catch (error) {
      toast.error('Failed to resolve report');
    }
  };

  const pendingReportsCount = reports.filter(r => r.status === 'pending').length;

  const handleRevokeFacilitator = async () => {
    if (!revokingFacilitator) return;
    try {
      await facilitatorAPI.revoke(revokingFacilitator.id, revokeReason);
      toast.success('Facilitator status revoked');
      setRevokeDialogOpen(false);
      setRevokingFacilitator(null);
      setRevokeReason('');
      await loadFacilitatorStatus();
      await loadGroups();
    } catch (error) {
      toast.error('Failed to revoke facilitator status');
    }
  };

  const handleReinstateFacilitator = async (userId) => {
    try {
      await facilitatorAPI.reinstate(userId);
      toast.success('Facilitator status reinstated');
      await loadFacilitatorStatus();
      await loadGroups();
    } catch (error) {
      toast.error('Failed to reinstate facilitator');
    }
  };

  const handleSuspendFacilitator = async () => {
    if (!suspendingFacilitator || !suspendReturnDate) return;
    try {
      await facilitatorAPI.suspend(suspendingFacilitator.id, suspendReturnDate, suspendReason);
      toast.success(`Facilitator suspended until ${new Date(suspendReturnDate).toLocaleDateString()}`);
      setSuspendDialogOpen(false);
      setSuspendingFacilitator(null);
      setSuspendReason('');
      setSuspendReturnDate('');
      await loadFacilitatorStatus();
    } catch (error) {
      toast.error('Failed to suspend facilitator');
    }
  };

  const handleEndSuspension = async (userId) => {
    try {
      await facilitatorAPI.endSuspension(userId);
      toast.success('Suspension ended - facilitator is now active');
      await loadFacilitatorStatus();
    } catch (error) {
      toast.error('Failed to end suspension');
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await groupsAPI.create(newGroupData);
      toast.success('Group created!');
      setCreateDialogOpen(false);
      setNewGroupData({ name: '', description: '', theme: '', max_members: 10 });
      await loadGroups();
      setActiveTab("my-groups");
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    if (!applicationData.proposed_group_theme) {
      toast.error('Please select a theme for your group');
      return;
    }
    try {
      await facilitatorAPI.apply(applicationData);
      toast.success('Application submitted! We\'ll review it soon.');
      setApplyDialogOpen(false);
      setApplicationData({
        reason: '',
        experience: '',
        proposed_group_name: '',
        proposed_group_theme: '',
        proposed_group_description: ''
      });
      await loadFacilitatorStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const handleReviewApplication = async (applicationId, approved) => {
    try {
      await facilitatorAPI.reviewApplication(applicationId, approved, adminMessage);
      toast.success(approved ? 'Application approved! Facilitator notified.' : 'Application rejected.');
      setReviewingApp(null);
      setAdminMessage('');
      await loadFacilitatorStatus();
      await loadGroups();
    } catch (error) {
      toast.error('Failed to review application');
    }
  };

  const handleAcceptFacilitator = async () => {
    try {
      await facilitatorAPI.accept();
      toast.success('Welcome, Facilitator! You can now edit and launch your group.');
      setApprovalDialogOpen(false);
      await loadFacilitatorStatus();
      await loadGroups();
    } catch (error) {
      toast.error('Failed to accept role');
    }
  };

  const handleLaunchGroup = async (groupId) => {
    try {
      await groupsExtendedAPI.launch(groupId);
      toast.success('Your group is now live! Members can start joining.');
      await loadGroups();
      await loadFacilitatorStatus();
    } catch (error) {
      toast.error('Failed to launch group');
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await groupsAPI.join(groupId);
      toast.success('Successfully joined group!');
      await loadGroups();
      setActiveTab("my-groups");
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join group');
    }
  };

  const handleLeaveGroup = async (groupId, e) => {
    e.stopPropagation();
    try {
      await groupsAPI.leave(groupId);
      toast.success('Left the group');
      await loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave group');
    }
  };

  const isInGroup = (groupId) => {
    return myGroups.some(g => g.id === groupId);
  };

  const canCreateGroups = facilitatorStatus?.is_approved_facilitator || facilitatorStatus?.is_admin;
  const hasPendingApplication = facilitatorStatus?.has_pending_application;
  const hasApprovedApplication = facilitatorStatus?.has_approved_application;
  const pendingApplications = applications.filter(a => a.status === 'pending');
  const unlauchedGroup = facilitatorStatus?.unlaunched_group;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  // Locked state for non-eligible users
  if (!hasAccess) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-16" data-testid="groups-locked">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
            <Lock size={32} className="text-muted-foreground" />
          </div>
          <h1 className="text-3xl font-serif mb-4">Small Groups</h1>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Join intimate support circles focused on growth themes. Connect with others on similar journeys, share experiences, and meet online.
          </p>
          <Button onClick={() => navigate('/pricing')} className="rounded-full">
            Unlock with Guided Path
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="groups-page">
        {/* Approval Dialog - shown when facilitator application is approved */}
        <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <PartyPopper className="text-green-600" size={24} />
                </div>
                <DialogTitle className="font-serif text-2xl">You&apos;re Approved!</DialogTitle>
              </div>
            </DialogHeader>
            
            {facilitatorStatus?.approved_application && (
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Congratulations! Your application to become a facilitator has been approved.
                </p>
                
                {facilitatorStatus.approved_application.admin_message && (
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                    <p className="text-sm font-medium mb-1">Message from Mo:</p>
                    <p className="text-sm text-muted-foreground">
                      {facilitatorStatus.approved_application.admin_message}
                    </p>
                  </div>
                )}
                
                <div className="bg-muted/50 rounded-xl p-4">
                  <p className="text-sm font-medium mb-2">Your Group (Draft):</p>
                  <p className="font-serif">{facilitatorStatus.approved_application.proposed_group_name}</p>
                  <p className="text-sm text-muted-foreground">{facilitatorStatus.approved_application.proposed_group_theme}</p>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  By accepting, you agree to facilitate your group with care and create a safe space for members.
                </p>
                
                <Button onClick={handleAcceptFacilitator} className="w-full rounded-full">
                  <CheckCircle size={16} className="mr-2" />
                  Accept Facilitator Role
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif mb-2">Small Groups</h1>
            <p className="text-muted-foreground">Connect with others on similar journeys</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Admin: Review Applications Badge */}
            {facilitatorStatus?.is_admin && pendingApplications.length > 0 && (
              <Button 
                variant="outline" 
                onClick={() => setActiveTab("admin")}
                className="rounded-full border-amber-500 text-amber-600"
              >
                <ClipboardList size={16} className="mr-2" />
                {pendingApplications.length} Pending
              </Button>
            )}
            
            {/* Create Group or Apply Button */}
            {canCreateGroups ? (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="create-group-btn">
                    <Plus size={16} className="mr-2" />
                    Create Group
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Create a New Group</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateGroup} className="space-y-4">
                    <div>
                      <Label htmlFor="group-name">Group Name</Label>
                      <Input
                        id="group-name"
                        value={newGroupData.name}
                        onChange={(e) => setNewGroupData({ ...newGroupData, name: e.target.value })}
                        placeholder="e.g., Morning Mindset Circle"
                        required
                        className="mt-2 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label htmlFor="group-theme">Theme</Label>
                      <Select
                        value={newGroupData.theme}
                        onValueChange={(value) => setNewGroupData({ ...newGroupData, theme: value })}
                      >
                        <SelectTrigger className="mt-2 rounded-xl">
                          <SelectValue placeholder="Select a theme" />
                        </SelectTrigger>
                        <SelectContent>
                          {GROUP_THEMES.map((theme) => (
                            <SelectItem key={theme} value={theme}>{theme}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="group-description">Description</Label>
                      <Textarea
                        id="group-description"
                        value={newGroupData.description}
                        onChange={(e) => setNewGroupData({ ...newGroupData, description: e.target.value })}
                        placeholder="What is this group about? Who should join?"
                        required
                        className="mt-2 rounded-xl"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="max-members">Max Members (4-10)</Label>
                      <Input
                        id="max-members"
                        type="number"
                        min={4}
                        max={10}
                        value={newGroupData.max_members}
                        onChange={(e) => setNewGroupData({ ...newGroupData, max_members: parseInt(e.target.value) })}
                        className="mt-2 rounded-xl"
                      />
                    </div>
                    <Button type="submit" className="w-full rounded-full">
                      Create Group
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            ) : hasPendingApplication ? (
              <Button disabled className="rounded-full" variant="outline">
                <Clock size={16} className="mr-2" />
                Application Pending
              </Button>
            ) : hasApprovedApplication ? (
              <Button onClick={() => setApprovalDialogOpen(true)} className="rounded-full bg-green-600 hover:bg-green-700">
                <PartyPopper size={16} className="mr-2" />
                View Approval
              </Button>
            ) : (
              <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="apply-facilitator-btn">
                    <ClipboardList size={16} className="mr-2" />
                    Apply to Facilitate
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Apply to Be a Facilitator</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground mb-4">
                    Facilitators lead small groups and create safe spaces for growth. Tell us about yourself and the group you&apos;d like to create.
                  </p>
                  <form onSubmit={handleSubmitApplication} className="space-y-4">
                    <div>
                      <Label htmlFor="app-reason">Why do you want to facilitate a group?</Label>
                      <Textarea
                        id="app-reason"
                        value={applicationData.reason}
                        onChange={(e) => setApplicationData({ ...applicationData, reason: e.target.value })}
                        placeholder="Share your motivation..."
                        required
                        className="mt-2 rounded-xl"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="app-experience">What relevant experience do you have?</Label>
                      <Textarea
                        id="app-experience"
                        value={applicationData.experience}
                        onChange={(e) => setApplicationData({ ...applicationData, experience: e.target.value })}
                        placeholder="Coaching, group leadership, personal journey..."
                        required
                        className="mt-2 rounded-xl"
                        rows={3}
                      />
                    </div>
                    <hr className="my-4" />
                    <p className="text-sm font-medium">Your Proposed Group</p>
                    <div>
                      <Label htmlFor="app-group-name">Group Name</Label>
                      <Input
                        id="app-group-name"
                        value={applicationData.proposed_group_name}
                        onChange={(e) => setApplicationData({ ...applicationData, proposed_group_name: e.target.value })}
                        placeholder="e.g., Morning Mindset Circle"
                        required
                        className="mt-2 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label htmlFor="app-group-theme">Theme</Label>
                      <Select
                        value={applicationData.proposed_group_theme}
                        onValueChange={(value) => setApplicationData({ ...applicationData, proposed_group_theme: value })}
                      >
                        <SelectTrigger className="mt-2 rounded-xl">
                          <SelectValue placeholder="Select a theme" />
                        </SelectTrigger>
                        <SelectContent>
                          {GROUP_THEMES.map((theme) => (
                            <SelectItem key={theme} value={theme}>{theme}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="app-group-description">Group Description</Label>
                      <Textarea
                        id="app-group-description"
                        value={applicationData.proposed_group_description}
                        onChange={(e) => setApplicationData({ ...applicationData, proposed_group_description: e.target.value })}
                        placeholder="What will this group focus on? Who should join?"
                        required
                        className="mt-2 rounded-xl"
                        rows={3}
                      />
                    </div>
                    <Button type="submit" className="w-full rounded-full">
                      Submit Application
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Unlaunched Group Banner */}
        {unlauchedGroup && (
          <Card className="p-6 rounded-2xl border-amber-300 bg-amber-50">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full">Draft</span>
                  <h3 className="font-serif text-lg">{unlauchedGroup.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your group is ready! Review the details and launch when you&apos;re ready for members to join.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/groups/${unlauchedGroup.id}`)}
                  className="rounded-full"
                >
                  <Pencil size={14} className="mr-2" />
                  Edit
                </Button>
                <Button 
                  onClick={() => handleLaunchGroup(unlauchedGroup.id)}
                  className="rounded-full bg-amber-600 hover:bg-amber-700"
                >
                  <Rocket size={14} className="mr-2" />
                  Launch Group
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full max-w-md rounded-full p-1 bg-muted/80 border border-border ${facilitatorStatus?.is_admin ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <TabsTrigger value="my-groups" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium">
              My Groups
            </TabsTrigger>
            <TabsTrigger value="discover" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium">
              Discover
            </TabsTrigger>
            {facilitatorStatus?.is_admin && (
              <TabsTrigger value="admin" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium">
                <Shield size={14} className="mr-1" />
                Admin
              </TabsTrigger>
            )}
          </TabsList>

          {/* My Groups Tab */}
          <TabsContent value="my-groups" className="mt-6">
            {myGroups.length === 0 ? (
              <Card className="p-12 text-center rounded-2xl">
                <Users className="mx-auto mb-4 opacity-50 text-muted-foreground" size={48} />
                <h3 className="text-xl font-serif mb-2">No Groups Yet</h3>
                <p className="text-muted-foreground mb-6">
                  Join an existing group or apply to create your own.
                </p>
                <Button variant="outline" onClick={() => setActiveTab("discover")} className="rounded-full">
                  Browse Groups
                </Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {myGroups.map((group) => (
                  <GroupCard
                    key={group.id}
                    group={group}
                    isMember={true}
                    currentUserId={user?.id}
                    onLeave={(e) => handleLeaveGroup(group.id, e)}
                    onLaunch={() => handleLaunchGroup(group.id)}
                    onClick={() => navigate(`/groups/${group.id}`)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Discover Tab */}
          <TabsContent value="discover" className="mt-6">
            {groups.length === 0 ? (
              <Card className="p-12 text-center rounded-2xl">
                <Users className="mx-auto mb-4 opacity-50 text-muted-foreground" size={48} />
                <h3 className="text-xl font-serif mb-2">No Groups Available</h3>
                <p className="text-muted-foreground mb-6">
                  New groups are coming soon. Check back later!
                </p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {groups.map((group) => (
                  <GroupCard
                    key={group.id}
                    group={group}
                    isMember={isInGroup(group.id)}
                    currentUserId={user?.id}
                    onJoin={() => handleJoinGroup(group.id)}
                    onLeave={(e) => handleLeaveGroup(group.id, e)}
                    onClick={() => isInGroup(group.id) ? navigate(`/groups/${group.id}`) : null}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Admin Tab */}
          {facilitatorStatus?.is_admin && (
            <TabsContent value="admin" className="mt-6 space-y-6">
              {/* Revoke Dialog */}
              <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
                <DialogContent className="rounded-2xl max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">Revoke Facilitator Status</DialogTitle>
                  </DialogHeader>
                  {revokingFacilitator && (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        This will <strong>permanently</strong> remove facilitator privileges from <strong>{revokingFacilitator.name}</strong> and deactivate their groups.
                      </p>
                      <div>
                        <Label htmlFor="revoke-reason">Reason (optional)</Label>
                        <Textarea
                          id="revoke-reason"
                          value={revokeReason}
                          onChange={(e) => setRevokeReason(e.target.value)}
                          placeholder="Reason for revoking facilitator status..."
                          className="mt-2 rounded-xl"
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleRevokeFacilitator}
                          className="rounded-full bg-red-600 hover:bg-red-700"
                        >
                          <UserX size={14} className="mr-2" />
                          Revoke Status
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => { setRevokeDialogOpen(false); setRevokingFacilitator(null); setRevokeReason(''); }}
                          className="rounded-full"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Suspend Dialog */}
              <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
                <DialogContent className="rounded-2xl max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">Suspend Facilitator</DialogTitle>
                  </DialogHeader>
                  {suspendingFacilitator && (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Temporarily suspend <strong>{suspendingFacilitator.name}</strong> from facilitating. Their groups will show as "facilitator on leave" until they return.
                      </p>
                      <div>
                        <Label htmlFor="suspend-return-date">Return Date *</Label>
                        <Input
                          id="suspend-return-date"
                          type="date"
                          value={suspendReturnDate}
                          onChange={(e) => setSuspendReturnDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          required
                          className="mt-2 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="suspend-reason">Reason (optional)</Label>
                        <Textarea
                          id="suspend-reason"
                          value={suspendReason}
                          onChange={(e) => setSuspendReason(e.target.value)}
                          placeholder="e.g., Personal leave, vacation, medical..."
                          className="mt-2 rounded-xl"
                          rows={2}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSuspendFacilitator}
                          disabled={!suspendReturnDate}
                          className="rounded-full bg-amber-600 hover:bg-amber-700"
                        >
                          <PauseCircle size={14} className="mr-2" />
                          Suspend Until {suspendReturnDate ? new Date(suspendReturnDate).toLocaleDateString() : '...'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => { setSuspendDialogOpen(false); setSuspendingFacilitator(null); setSuspendReason(''); setSuspendReturnDate(''); }}
                          className="rounded-full"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Applications Section */}
              <Card className="p-6 rounded-2xl">
                <h3 className="text-xl font-serif mb-4">Facilitator Applications</h3>
                
                {applications.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No applications yet.</p>
                ) : (
                  <div className="space-y-3">
                    {applications.map((app) => (
                      <div key={app.id} className={`rounded-xl border overflow-hidden ${
                        app.status === 'pending' ? 'border-amber-300 bg-amber-50' : 
                        app.status === 'approved' ? 'border-green-300 bg-green-50' : 
                        'border-red-300 bg-red-50'
                      }`}>
                        {/* Application Header - Always Visible */}
                        <button
                          onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                          className="w-full p-4 flex items-center justify-between hover:bg-black/5 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="text-left">
                              <p className="font-medium">{app.user_name}</p>
                              <p className="text-xs text-muted-foreground">{app.user_email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              app.status === 'pending' ? 'bg-amber-200 text-amber-800' :
                              app.status === 'approved' ? 'bg-green-200 text-green-800' :
                              'bg-red-200 text-red-800'
                            }`}>
                              {app.status}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(app.created_at).toLocaleDateString()}
                            </span>
                            {expandedApp === app.id ? (
                              <ChevronUp size={16} className="text-muted-foreground" />
                            ) : (
                              <ChevronDown size={16} className="text-muted-foreground" />
                            )}
                          </div>
                        </button>
                        
                        {/* Expanded Application Details */}
                        {expandedApp === app.id && (
                          <div className="px-4 pb-4 border-t border-current/10">
                            <div className="space-y-3 text-sm mt-4">
                              <div>
                                <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-1">Why they want to facilitate</p>
                                <p>{app.reason}</p>
                              </div>
                              <div>
                                <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-1">Experience</p>
                                <p>{app.experience}</p>
                              </div>
                              <div className="bg-white/50 p-3 rounded-lg">
                                <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-1">Proposed Group</p>
                                <p className="font-serif">{app.proposed_group_name}</p>
                                <p className="text-muted-foreground text-xs">{app.proposed_group_theme}</p>
                                <p className="mt-1">{app.proposed_group_description}</p>
                              </div>
                            </div>
                            
                            {app.status === 'pending' && (
                              reviewingApp === app.id ? (
                                <div className="space-y-3 mt-4 pt-4 border-t border-current/10">
                                  <div>
                                    <Label htmlFor={`msg-${app.id}`}>Message to Facilitator</Label>
                                    <Textarea
                                      id={`msg-${app.id}`}
                                      value={adminMessage}
                                      onChange={(e) => setAdminMessage(e.target.value)}
                                      placeholder="Welcome message, instructions, or guidance..."
                                      className="mt-2 rounded-xl"
                                      rows={3}
                                    />
                                  </div>
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      onClick={() => handleReviewApplication(app.id, true)}
                                      className="rounded-full bg-green-600 hover:bg-green-700"
                                    >
                                      <CheckCircle size={14} className="mr-1" />
                                      Approve
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleReviewApplication(app.id, false)}
                                      className="rounded-full text-red-600 border-red-300 hover:bg-red-50"
                                    >
                                      <XCircle size={14} className="mr-1" />
                                      Reject
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => { setReviewingApp(null); setAdminMessage(''); }}
                                      className="rounded-full"
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="mt-4 pt-4 border-t border-current/10">
                                  <Button 
                                    size="sm" 
                                    onClick={() => setReviewingApp(app.id)}
                                    className="rounded-full"
                                  >
                                    Review Application
                                  </Button>
                                </div>
                              )
                            )}
                            
                            {app.admin_message && app.status === 'approved' && (
                              <div className="mt-4 p-3 bg-white/50 rounded-lg">
                                <p className="text-xs font-medium text-muted-foreground">Your message:</p>
                                <p className="text-sm">{app.admin_message}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Active Facilitators Section */}
              <Card className="p-6 rounded-2xl">
                <h3 className="text-xl font-serif mb-4">Active Facilitators</h3>
                
                {facilitators.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No facilitators yet.</p>
                ) : (
                  <div className="space-y-3">
                    {facilitators.map((f) => (
                      <div key={f.id} className={`p-4 rounded-xl border ${f.is_suspended ? 'border-amber-300 bg-amber-50' : 'bg-muted/30'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{f.name}</p>
                              {f.is_suspended && (
                                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <PauseCircle size={10} />
                                  On Leave
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{f.email}</p>
                            
                            {f.is_suspended && f.suspension_return_date && (
                              <div className="mt-2 p-2 bg-amber-100 rounded-lg">
                                <p className="text-xs text-amber-800 flex items-center gap-1">
                                  <Calendar size={12} />
                                  Returns: {new Date(f.suspension_return_date).toLocaleDateString()}
                                </p>
                                {f.suspension_reason && (
                                  <p className="text-xs text-amber-700 mt-1">Reason: {f.suspension_reason}</p>
                                )}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                {f.group_count} group{f.group_count !== 1 ? 's' : ''}
                              </span>
                              {f.groups?.slice(0, 2).map((g) => (
                                <span key={g.id} className="text-xs text-muted-foreground">
                                  {g.name}
                                </span>
                              ))}
                              {f.groups?.length > 2 && (
                                <span className="text-xs text-muted-foreground">+{f.groups.length - 2} more</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {f.is_suspended ? (
                              <Button
                                size="sm"
                                onClick={() => handleEndSuspension(f.id)}
                                className="rounded-full bg-green-600 hover:bg-green-700"
                              >
                                <PlayCircle size={14} className="mr-1" />
                                End Leave
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setSuspendingFacilitator(f); setSuspendDialogOpen(true); }}
                                className="rounded-full text-amber-600 border-amber-300 hover:bg-amber-50"
                              >
                                <PauseCircle size={14} className="mr-1" />
                                Suspend
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setRevokingFacilitator(f); setRevokeDialogOpen(true); }}
                              className="rounded-full text-red-600 border-red-300 hover:bg-red-50"
                            >
                              <UserX size={14} className="mr-1" />
                              Revoke
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Reports Section */}
              <Card className="p-6 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-serif flex items-center gap-2">
                    <Flag className="text-red-500" size={20} />
                    Reports
                    {pendingReportsCount > 0 && (
                      <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                        {pendingReportsCount} pending
                      </span>
                    )}
                  </h3>
                </div>
                
                {reports.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No reports yet.</p>
                ) : (
                  <div className="space-y-3">
                    {reports.map((report) => (
                      <div key={report.id} className={`rounded-xl border overflow-hidden ${
                        report.status === 'pending' ? 'border-red-300 bg-red-50' : 
                        report.status === 'resolved' ? 'border-green-300 bg-green-50' : 
                        'border-gray-300 bg-gray-50'
                      }`}>
                        {/* Report Header - Always Visible */}
                        <button
                          onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
                          className="w-full p-4 flex items-center justify-between hover:bg-black/5 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              report.report_type === 'post' ? 'bg-blue-100' :
                              report.report_type === 'member' ? 'bg-purple-100' :
                              'bg-orange-100'
                            }`}>
                              {report.report_type === 'post' ? <MessageSquare size={14} className="text-blue-600" /> :
                               report.report_type === 'member' ? <User size={14} className="text-purple-600" /> :
                               <Users size={14} className="text-orange-600" />}
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-sm">{report.target_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {report.report_type} in {report.group_name}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                              report.status === 'pending' ? 'bg-red-200 text-red-800' :
                              report.status === 'resolved' ? 'bg-green-200 text-green-800' :
                              'bg-gray-200 text-gray-800'
                            }`}>
                              {report.status}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(report.created_at).toLocaleDateString()}
                            </span>
                            {expandedReport === report.id ? (
                              <ChevronUp size={16} className="text-muted-foreground" />
                            ) : (
                              <ChevronDown size={16} className="text-muted-foreground" />
                            )}
                          </div>
                        </button>
                        
                        {/* Expanded Report Details */}
                        {expandedReport === report.id && (
                          <div className="px-4 pb-4 border-t border-current/10">
                            <div className="space-y-3 text-sm mt-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-1">Category</p>
                                  <p className="capitalize">{report.category.replace('_', ' ')}</p>
                                </div>
                                <div>
                                  <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-1">Reported by</p>
                                  <p>{report.reporter_name}</p>
                                  <p className="text-xs text-muted-foreground">{report.reporter_email}</p>
                                </div>
                              </div>
                              <div>
                                <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-1">Description</p>
                                <p className="bg-white/50 p-3 rounded-lg">{report.description}</p>
                              </div>
                            </div>
                            
                            {report.status === 'pending' && (
                              <div className="space-y-3 mt-4 pt-4 border-t border-current/10">
                                <div>
                                  <Label htmlFor={`notes-${report.id}`}>Admin Notes (optional)</Label>
                                  <Textarea
                                    id={`notes-${report.id}`}
                                    value={resolveNotes}
                                    onChange={(e) => setResolveNotes(e.target.value)}
                                    placeholder="Notes about the action taken..."
                                    className="mt-2 rounded-xl"
                                    rows={2}
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    onClick={() => handleResolveReport(report.id, 'resolved')}
                                    className="rounded-full bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle size={14} className="mr-1" />
                                    Mark Resolved
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleResolveReport(report.id, 'dismissed')}
                                    className="rounded-full"
                                  >
                                    <XCircle size={14} className="mr-1" />
                                    Dismiss
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {report.admin_notes && report.status !== 'pending' && (
                              <div className="mt-4 p-3 bg-white/50 rounded-lg">
                                <p className="text-xs font-medium text-muted-foreground">Admin notes:</p>
                                <p className="text-sm">{report.admin_notes}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function GroupCard({ group, isMember, currentUserId, onJoin, onLeave, onLaunch, onClick }) {
  const memberCount = group.members?.length || 0;
  const isFull = memberCount >= group.max_members;
  const isFacilitator = group.facilitator_id === currentUserId;
  const isDraft = !group.is_launched;

  return (
    <Card 
      className={`p-6 rounded-2xl border-border hover:shadow-md transition-shadow ${isMember ? 'cursor-pointer' : ''} ${isDraft ? 'border-amber-300 bg-amber-50/50' : ''}`}
      onClick={isMember ? onClick : undefined}
      data-testid={`group-card-${group.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
              {group.theme}
            </span>
            {isDraft && (
              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full">
                Draft
              </span>
            )}
          </div>
          <h3 className="text-lg font-serif mt-2">{group.name}</h3>
        </div>
        {isFacilitator && (
          <span className="text-xs bg-accent/20 text-accent px-2 py-1 rounded-full">
            Facilitator
          </span>
        )}
      </div>
      
      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
        {group.description}
      </p>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users size={14} />
          <span>{memberCount}/{group.max_members} members</span>
        </div>
        
        {isMember ? (
          <div className="flex items-center gap-2">
            {isDraft && isFacilitator && onLaunch && (
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); onLaunch(); }}
                className="rounded-full bg-amber-600 hover:bg-amber-700"
              >
                <Rocket size={14} className="mr-1" />
                Launch
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onLeave}
              className="rounded-full text-muted-foreground hover:text-destructive"
            >
              <LogOut size={14} className="mr-1" />
              Leave
            </Button>
            <ArrowRight size={16} className="text-muted-foreground" />
          </div>
        ) : (
          <Button
            size="sm"
            onClick={onJoin}
            disabled={isFull}
            className="rounded-full"
          >
            {isFull ? 'Full' : (
              <>
                <UserPlus size={14} className="mr-1" />
                Join
              </>
            )}
          </Button>
        )}
      </div>
    </Card>
  );
}
